import { useCallback, useMemo, useState } from 'react';
import type { DataTableSelection } from './types.js';

export type PageSelectionState = 'none' | 'some' | 'all';

export function getPageSelectionState(
  selectedKeys: ReadonlySet<string>,
  pageKeys: readonly string[],
): PageSelectionState {
  if (pageKeys.length === 0) return 'none';
  let count = 0;
  for (const k of pageKeys) {
    if (selectedKeys.has(k)) count += 1;
  }
  if (count === 0) return 'none';
  if (count === pageKeys.length) return 'all';
  return 'some';
}

export function togglePageSelection(
  selectedKeys: ReadonlySet<string>,
  pageKeys: readonly string[],
): Set<string> {
  const state = getPageSelectionState(selectedKeys, pageKeys);
  const next = new Set(selectedKeys);
  if (state === 'all') {
    for (const k of pageKeys) next.delete(k);
  } else {
    for (const k of pageKeys) next.add(k);
  }
  return next;
}

export function countSelectedOnPage(
  selectedKeys: ReadonlySet<string>,
  pageKeys: readonly string[],
): number {
  let n = 0;
  for (const k of pageKeys) {
    if (selectedKeys.has(k)) n += 1;
  }
  return n;
}

export function useDataTableSelection(): {
  selectedKeys: Set<string>;
  onSelectionChange: (keys: Set<string>) => void;
  clear: () => void;
  count: number;
  selection: DataTableSelection<unknown>;
} {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());

  const onSelectionChange = useCallback((keys: Set<string>) => {
    setSelectedKeys(new Set(keys));
  }, []);

  const clear = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  const selection = useMemo(
    (): DataTableSelection<unknown> => ({
      selectedKeys,
      onSelectionChange,
    }),
    [selectedKeys, onSelectionChange],
  );

  return {
    selectedKeys,
    onSelectionChange,
    clear,
    count: selectedKeys.size,
    selection,
  };
}
